'use client';

import React from 'react';
import { ChevronRight } from 'lucide-react';
import useGetHeroImageByTag from '@/components/hooks/use-get-hero-image-by-tag';
import BackgroundImage from '@/components/ui/layout/background-image';
import Container from '@/components/ui/layout/container';
import Flex from '@/components/ui/layout/flex';
import Section from '@/components/ui/layout/section';
import Spacer from '@/components/ui/layout/spacer';
import { MotionLink } from '@/components/ui/Motion';
import {
  TypographyH1,
  TypographyH2,
  TypographySmall,
} from '@/components/ui/typography';
import { cls } from '@/lib/utils';
import type { ProjectData } from '../../data/project-data';

interface ProjectsHeroProperties {
  project: ProjectData;
}

function ProjectHero({ project }: ProjectsHeroProperties) {
  const tag = `hero_projects_${project.urlSlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('_')}`;
  const { data: heroImage } = useGetHeroImageByTag({ tag });

  const content = (
    <>
      <Spacer size="h-[50px]" />

      {/* Breadcrumbs */}
      <nav
        aria-label="Breadcrumb"
        className={cls`flex items-center gap-1 text-sm mb-6 justify-center ${
          heroImage ? 'text-white/70' : 'text-muted-foreground'
        }`}
      >
        <MotionLink
          href="/"
          className={
            heroImage
              ? 'hover:text-white transition-colors'
              : 'hover:text-foreground transition-colors'
          }
        >
          Home
        </MotionLink>
        <ChevronRight className="size-4" />
        <MotionLink
          href="/projects"
          className={
            heroImage
              ? 'hover:text-white transition-colors'
              : 'hover:text-foreground transition-colors'
          }
        >
          Projects
        </MotionLink>
        <ChevronRight className="size-4" />
        <span
          className={
            heroImage
              ? 'text-white font-medium'
              : 'text-foreground font-medium'
          }
        >
          {project.name}
        </span>
      </nav>

      {/* Header */}
      <Flex direction="column" gap="gap-2">
        <Container maxWidth="max-w-2xl">
          <Flex
            direction="column"
            gap="gap-2 md:gap-4 lg:gap-6"
            className="text-center"
          >
            <TypographyH2 className="font-bold border-b-0">
              {project.style.toUpperCase()}
            </TypographyH2>

            <TypographyH1>{project.name}</TypographyH1>

            <TypographySmall>{project.heroDescription}</TypographySmall>

            {/* Spec badges */}
            {project.sqft && (
              <div className={cls`flex flex-wrap justify-center gap-3 mt-2`}>
                {project.sqft && (
                  <span className="bg-primary text-primary-foreground text-sm px-4 py-1.5 rounded-sm font-medium">
                    {project.sqft} Sq. Ft.
                  </span>
                )}
                {project.beds && (
                  <span className="bg-primary text-primary-foreground text-sm px-4 py-1.5 rounded-sm font-medium">
                    {project.beds} Beds
                  </span>
                )}
                {project.baths && (
                  <span className="bg-primary text-primary-foreground text-sm px-4 py-1.5 rounded-sm font-medium">
                    {project.baths} Baths
                  </span>
                )}
                {project.garage && (
                  <span className="bg-primary text-primary-foreground text-sm px-4 py-1.5 rounded-sm font-medium">
                    {project.garage}
                  </span>
                )}
              </div>
            )}
          </Flex>
        </Container>
      </Flex>
    </>
  );

  if (!heroImage) {
    return (
      <Section id="hero" className="bg-card">
        <Container id="hero" maxWidth="max-w-5xl">
          {content}
        </Container>
      </Section>
    );
  }

  return (
    <Section id="hero" className="relative min-h-[80vh]">
      <BackgroundImage
        src={heroImage}
        alt={`${project.name} Project`}
        fill
        sizes="100vw"
        overlay="bg-black/40"
      >
        <Container id="hero" maxWidth="max-w-5xl" className="text-white">
          {content}
        </Container>
      </BackgroundImage>
    </Section>
  );
}

export default ProjectHero;
